<template>
  <div :class="['field', { required: required }]">
    <transition name="fade">
      <label v-if="title" :title="title">
        {{ title }}
        <tooltip v-if="hint" :text="hint"></tooltip>
      </label>
    </transition>
    <input
      ref="widget"
      style="width: 100%; padding: 0; height: 32px; border: none;"
      type="date"
      :name="name"/>
  </div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';

export default {
  props: {
    hint: String,
    depth: String,
    label: String,
    name: String,
    max: [Date, Boolean],
    min: [Date, Boolean],
    dataTextField: {
      type: String,
      default: 'text'
    },
    dataValueField: {
      type: String,
      default: 'value'
    },
    dataSource: Array,
    placeholder: String,
    format: {
      type: String,
      default: 'd/M/yyyy'
    },
    outputFormat: {
      type: String
    },
    inputFormat: {
      type: String
    },
    required: Boolean,
    enable: {
      type: Boolean,
      default: true
    },
    value: {
      type: [Date, Object],
      default: () => new Date()
    }
  },

  data () {
    const config = {
      change: this.onChange,
      format: this.format,
      value: new Date(),
      enable: this.enable,
      start: {
        type: String,
        default: 'month'
      }
    };

    let dataSource;

    if (this.dataSource) {
      dataSource = _.map(this.dataSource, d => {
        const now = new Date(d[this.dataValueField]);
        const timezoneOffset = new Date().getTimezoneOffset() / 60;
        return moment(now).add(timezoneOffset, 'hours').toDate();
      });
      config.disableDates = (date) => {
        if (_.isDate(date)) {
          return !_.some(dataSource, d => kendo.toString(d, 'yyyyMMdd') === kendo.toString(date, 'yyyyMMdd'));
        }

        return false;
      };
    }

    if (this.value) {
      config.value = this.parseDate(this.value);
    }

    if (this.min) {
      const min = dataSource ? _.min(dataSource) : this.min;

      if (_.isBoolean(this.min) && this.dataSource) {
        config.min = moment(min).startOf('month').toDate();
      } else {
        config.min = this.min;
      }

      if (min.getTime() > config.value.getTime()) {
        config.value = min;
      }
    }

    if (this.max) {
      const max = dataSource ? _.max(dataSource) : this.max;

      if (_.isBoolean(this.max) && this.dataSource) {
        config.max = moment(max).endOf('month').toDate();
      } else {
        config.max = this.max;
      }

      if (max.getTime() < config.value.getTime()) {
        config.value = max;
      }
    }

    if (this.depth) {
      config.depth = this.depth;
      config.start = this.depth;
    }

    return {
      title: this.value ? this.label || this.placeholder : '',
      widget: undefined,
      config
    };
  },

  mounted () {
    this.widget = new kendo.ui.DatePicker(this.$refs.widget, this.config);
    this.onChange();
  },

  destroyed () {
    if (this.widget) {
      this.widget.destroy();
    }
  },

  methods: {
    onChange (e) {
      const data = this.formatData();
      this.$emit('change', data);
      this.$emit('input', data);
    },

    hasValue (value) {
      return _.isArray(value) ? _.size(value) : !isNaN(Number(value)) ? true : !!value;
    },

    setValue (date) {
      this.widget.value(date);
    },

    parseDate (date) {
      if (date) {
        if (_.isDate(date)) {
          return date;
        } else if (_.isString(date)) {
          return new Date(date);
        } else if (this.hasValue(date.value)) {
          return _.isArray(date.value) ? kendo.parseDate(date.value[0], this.inputFormat) : kendo.parseDate(date.value, this.inputFormat);
        }
      }
      return new Date();
    },

    formatData () {
      const value = this.widget.value();
      return {
        metadata: {
          name: this.name,
          text: this.dataTextField,
          value: this.dataValueField,
          properties: Object.keys((this.dataSource && this.dataSource[0]) || {})
        },
        value: this.outputFormat ? kendo.toString(value, this.outputFormat) : value,
        text: kendo.toString(value, 'd')
      };
    }
  },

  watch: {
    value (value) {
      if (value) {
        this.title = this.label || this.placeholder;
        this.setValue(this.parseDate(value));
      } else {
        this.title = '';
      }
    },

    enable (val) {
      this.widget.setOptions({ enable: val });
      this.widget.enable(val);
    },

    min(min){
      this.widget.min(min);

      const diffMonths = moment(this.widget.value()).diff(min, 'month');
      const shouldUpdateValue = diffMonths < 0;
      if(shouldUpdateValue)
        this.setValue(min);
    }
  }
};
</script>

<style lang="scss" scoped>
  // Animations
  .fade-enter-active, .fade-leave-active {
    transition: opacity .5s
  }

  .fade-enter, .fade-leave-active {
    opacity: 0
  }
</style>
